import FancytreeNode = Fancytree.FancytreeNode;
import {BaseTree, BaseTreeNodeData} from "../ui/baseTree";
import {Identifiable, TreeNode} from "@/shared/timr";

export class AssignmentTree<T extends Identifiable> {
	private tree: Fancytree.Fancytree & FancyTreeTreeAssignExtension;
	private partial: boolean = false;

	constructor(private baseTree: BaseTree, onAssignment: ((node: FancytreeNode) => void) | false, onUnassignment?: ((node: FancytreeNode) => void) | false, partial ?: boolean) {
		this.tree = baseTree["tree"] as Fancytree.Fancytree & FancyTreeTreeAssignExtension;
		this.tree.setAssignmentCallback(onAssignment);
		if (onUnassignment) {
			this.tree.setUnAssignmentCallback(onUnassignment);
		}
		if (partial) {
			this.partial = true;
			this.tree.setPartialMode(true);
		}
		this.tree.activateAssignmentExtension();
	}

	public assign(t: T) {
		this.tree.assign("" + t.id);
	}

	public unassign(t: T) {
		this.tree.unassign("" + t.id);
	}

	public isAssigned(t: T): boolean {
		return this.tree.isAssigned("" + t.id);
	}

	public setAssigned(ts: (T | string)[]) {
		this.tree.setAssigned(ts.map(t => {
			if (typeof t === "object") {
				return "" + t.id;
			} else {
				return t;
			}
		}));
	}

	public getBaseTree(): BaseTree {
		return this.baseTree;
	}

	public getAssigned(): string[] {
		return this.tree.getAssigned();
	}

	public addTask(node: T & TreeNode, parentAcessor: (node: T & TreeNode) => (T & TreeNode) | null) {
		const parentTask = parentAcessor(node);

		if (parentTask != null) {
			this.addTask(parentTask, parentAcessor);
		}

		const myNode = this.getBaseTree().getNodeByKey(node.id + "");

		if (myNode == null) {
			node.lazy = false;
			if (parentTask == null) {
				const rootNode = this.getBaseTree().getRoot();

				if (!rootNode.isLoading()) {
					// i am in root and have to add myself
					rootNode.addNode(node);
				}
			} else {
				const parentNode = this.getBaseTree().getNodeByKey(parentTask.id + "");
				parentNode.addNode(node);
				parentNode.setExpanded(true);
			}
		} else {
			if (node.assignedToCurrentUser) {
				const myNodeData = myNode.data as TreeNode;
				myNodeData.assignedToCurrentUser = true;
			}

			if (myNode.isRootNode() && !myNode.isLoaded()) {
				myNode.load();
			}

			myNode.render();
		}
	}
}

export interface FancyTreeTreeAssignExtension {
	assign(key: string): void;

	unassign(key: string): void;

	isAssigned(key: string): boolean;

	getAssigned(): string[];

	setAssigned(keys: string[]): void;

	setAssignmentCallback(callback: ((node: FancytreeNode) => void) | false): void;

	setUnAssignmentCallback(callback: ((node: FancytreeNode) => void) | false): void;

	activateAssignmentExtension(): void;

	deActivateAssignmentExtension(): void;

	setPartialMode(partial: boolean): void;
}


/** State the `treeassign` extension keeps on the fancytree instance (`this.ext.treeassign` / hook `this._local`). */
interface TreeAssignData {
	active: boolean;
	initialized: boolean;
	serverLoaded: string[];
	assignmentDict: Record<string, boolean>;
	assignCallback: ((node: FancytreeNode) => void) | false;
	unassignCallback: ((node: FancytreeNode) => void) | false;
	partial: boolean;
}

/** The fancytree instance as seen by the extension methods (`this`), augmented with the treeassign state. */
type TreeAssignInstance = Fancytree.Fancytree & FancyTreeTreeAssignExtension & { ext: { treeassign: TreeAssignData } };

/** Hook context (`this`) inside `registerExtension` hooks: base method access plus the per-instance state. */
interface TreeAssignHookContext {
	_super(ctx: Fancytree.EventData, force?: boolean, deep?: boolean, collapsed?: boolean): void;
	_local: TreeAssignData;
}


// === Implementation of FancyTree extension ===

(function (factory) {
	// This extension runs in the browser where fancytree is loaded globally.
	// Avoid legacy AMD/CJS local requires that break Rolldown module resolution.
	factory(jQuery);
})(function ($: JQueryStatic) {
	// `_FancytreeClass` is fancytree's internal class, not modelled by @types/jquery.fancytree.
	const fancytree = $.ui.fancytree as Fancytree.FancytreeStatic & {
		_FancytreeClass: {prototype: TreeAssignInstance};
	};
	const fancytreeClassPrototype = fancytree._FancytreeClass.prototype;
	function setSpanIconRemoveable(node: Fancytree.FancytreeNode) {
		if (node?.span) {
			const span = $(node.span);
			span.find("i.assignment")
				.addClass("fa-times-square");
			span.find("a")
				.removeClass("disabled");
		}
	}

	function setSpanIconNotRemoveable(node: Fancytree.FancytreeNode) {
		if (node?.span && node.statusNodeType !== "loading") {
			const span = $(node.span);
			span.find("i.assignment")
				.addClass("fa-times-square");
			span.find("a")
				.addClass("disabled");
		}
	}

	function setSpanIconAssigned(node: Fancytree.FancytreeNode) {
		if (node?.span) {
			$(node.span).find("i.assignment")
				.addClass("fa-check-square")
				.removeClass("fa-plus-square");
		}
	}

	function setSpanIconUnassigned(node: Fancytree.FancytreeNode) {
		if (node?.span && node.statusNodeType !== "loading") {
			$(node.span).find("i.assignment")
				.removeClass("fa-check-square")
				.addClass("fa-plus-square");
		}
	}

	fancytreeClassPrototype.assign = function (key: string) {
		this.ext.treeassign.assignmentDict[key] = true;
		const node = this.getNodeByKey(key);
		setSpanIconAssigned(node);
	};

	fancytreeClassPrototype.setAssigned = function (keys: string[]) {
		const oldAssigned: string[] = this.getAssigned();

		this.ext.treeassign.assignmentDict = {};
		keys.forEach(key => this.ext.treeassign.assignmentDict[key] = true);

		const newlyAssigned = keys.filter(v => oldAssigned.indexOf(v) === -1);
		const unassigned = oldAssigned.filter(v => keys.indexOf(v) === -1);

		newlyAssigned
			.map(key => this.getNodeByKey(key))
			.forEach(node => setSpanIconAssigned(node));

		unassigned
			.map(key => this.getNodeByKey(key))
			.forEach(node => setSpanIconUnassigned(node));

	};

	fancytreeClassPrototype.unassign = function (key: string) {
		this.ext.treeassign.assignmentDict[key] = false;
		const node = this.getNodeByKey(key);
		setSpanIconUnassigned(node);
	};

	fancytreeClassPrototype.isAssigned = function (key: string) {
		return this.ext.treeassign.assignmentDict[key] === true;
	};

	fancytreeClassPrototype.getAssigned = function (): string[] {
		const dict = this.ext.treeassign.assignmentDict;
		return Object.keys(dict)
			.filter(key => dict[key] === true);
	};

	fancytreeClassPrototype.setAssignmentCallback = function (callback) {
		this.ext.treeassign.assignCallback = callback;
	};

	fancytreeClassPrototype.setUnAssignmentCallback = function (callback) {
		this.ext.treeassign.unassignCallback = callback;
	};

	fancytreeClassPrototype.activateAssignmentExtension = function () {
		this.ext.treeassign.active = true;
	};

	fancytreeClassPrototype.deActivateAssignmentExtension = function () {
		this.ext.treeassign.active = false;
	};

	fancytreeClassPrototype.setPartialMode = function (partial) {
		this.ext.treeassign.partial = partial;
	};

	fancytree.registerExtension({
		name: "treeassign",
		version: "0.0.1",
		nodeRender: function (this: TreeAssignHookContext, ctx: Fancytree.EventData, force: boolean, deep: boolean, collapsed: boolean) {
			this._super(ctx, force, deep, collapsed);

			const local = this._local;
			if (!local.active) {
				return;
			}

			const node = ctx.node;
			const nodeSpan = $(node.span);
			const nodeData = node.data as BaseTreeNodeData;

			if (!nodeSpan.data("rendered-assign") && !nodeData?.dummy && local.assignCallback) {
				nodeSpan.data("node-key", node.key)
					.data("rendered-assign", true)
					.children()
					.eq(0).after(`<a class="ml-5"><i class="fal node-icon assignment"></i></a>`);
			}

			if (local.partial) {
				const treeNode = node.data as TreeNode;
				if (treeNode?.assignedToCurrentUser) {
					setSpanIconRemoveable(node);
				} else {
					setSpanIconNotRemoveable(node);
				}
			} else {
				if (local.assignmentDict[node.key] === true) {
					setSpanIconAssigned(node);
				} else {
					setSpanIconUnassigned(node);
				}
			}
		},
		nodeClick: function (this: TreeAssignHookContext, ctx: Fancytree.EventData) {
			const local = this._local;
			if (!local.active) {
				this._super(ctx);
				return true;
			}

			const node = ctx.node;
			if (node) {

				const element = $(ctx.originalEvent.target);
				if (element.is("i.assignment")) {
					if (local.partial) {
						/**
						 * Notifies the unassign callback of the nodes removal.
						 * This method propagates down the tree and also visits loaded children
						 * as these are also added to the assigned list upon lazy loading.
						 * @param nodeToRemove
						 */
						const notifyRemove = (nodeToRemove: FancytreeNode) => {
							const nodeData = nodeToRemove.data as TreeNode;
							if (nodeData?.assignedToCurrentUser && local.unassignCallback) {
								local.unassignCallback(nodeToRemove);
							}

							// If the node is a lazy node and has not been loaded then it does not have children.
							// That also means the children have not been added to the assigned list yet so we don't
							// need to unassign them.
							nodeToRemove.children?.forEach(notifyRemove);
						};

						/**
						 * Cleans up the node tree by going upwards in the tree and removing all nodes
						 * that are not assigned and don't have children.
						 * @param nodeToRemove
						 */
						const removeParentNodesNotAssigned = (nodeToRemove: FancytreeNode) => {
							const nodeData = nodeToRemove.data as TreeNode;
							if (!nodeToRemove.hasChildren() && !nodeData?.assignedToCurrentUser) {
								const parent = nodeToRemove.getParent();
								if (parent) {
									nodeToRemove.remove();
									removeParentNodesNotAssigned(parent);
								}
							}
						};

						const parent = node.getParent();
						notifyRemove(node);
						node.remove();
						if (parent) {
							removeParentNodesNotAssigned(parent);
						}

						// Prevent extension of node by not calling super clickhandler
						return false;
					} else {
						if (local.assignmentDict[node.key] !== true) {

							setSpanIconAssigned(node);

							local.assignmentDict[node.key] = true;
							// reachable only in non-partial mode, where an assign callback is always registered
							const assignCallback = local.assignCallback as (node: FancytreeNode) => void;
							assignCallback(node);

							// Prevent extension of node by not calling super clickhandler
							return false;
						}
					}
				}
			}

			this._super(ctx);
		},
		treeInit: function (this: TreeAssignHookContext, ctx: Fancytree.EventData) {
			const local = this._local;

			// local variables initialization
			// they can't be added on the extension object itself since
			// it gets copied to each instance and the assignment dict
			// for example would be the same instance for tree
			local.assignmentDict = {};
			local.initialized = false;
			local.active = false;
			local.serverLoaded = [];
			local.assignCallback = false;
			local.unassignCallback = false;
			local.partial = false;

			$(ctx.tree.$div).on("fancytreeloadchildren", function (event: JQuery.Event, eventContext: Fancytree.EventData) {
				if (local.partial) {
					function nodeLoad(node: Fancytree.FancytreeNode) {
						if (node.children) {
							node.children.forEach(nodeLoad);
						}

						if (node.data) {
							if (!local.serverLoaded.some(k => k === node.key)) {
								local.serverLoaded.push(node.key);
							}

							const wasAssigned = local.assignmentDict[node.key];
							const nodeData = node.data as TreeNode;
							if (nodeData.assignedToCurrentUser) {
								local.assignmentDict[node.key] = true;
								if (!wasAssigned && local.assignCallback) {
									local.assignCallback(node);
								}
							} else {
								local.assignmentDict[node.key] = false;
								if (wasAssigned && local.unassignCallback) {
									local.unassignCallback(node);
								}
							}
						}
					}

					nodeLoad(eventContext.node);
				}
			}).on("fancytreeinit", function () {
				local.initialized = true;
			});

			this._super(ctx);
		}
	});

	return fancytree;
});
